import { useState, useRef, useCallback, useEffect } from "react";
import { ReactCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const ImageCropper = ({
  initialSrc,
  onCropComplete,
  onCancel,
  circular = true,
  targetSize = 158,
}) => {
  const [src, setSrc] = useState(initialSrc || null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (initialSrc) {
      console.log("[Cropper] initialSrc provided", initialSrc?.slice(0, 32));
      setSrc(initialSrc);
    }
  }, [initialSrc]);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        console.log(
          "[Cropper] file loaded to dataURL",
          String(reader.result).slice(0, 32)
        );
        setSrc(reader.result);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const getCroppedImg = useCallback(
    (image, crop) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = targetSize;
      canvas.height = targetSize;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      if (circular) {
        ctx.beginPath();
        ctx.arc(targetSize / 2, targetSize / 2, targetSize / 2, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.clip();
      }

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        targetSize,
        targetSize
      );

      return canvas.toDataURL("image/png");
    },
    [circular, targetSize]
  );

  const handleConfirm = useCallback(() => {
    console.log("[Cropper] confirm clicked", completedCrop);
    if (
      imgRef.current &&
      completedCrop &&
      completedCrop.width &&
      completedCrop.height
    ) {
      try {
        const croppedImageUrl = getCroppedImg(imgRef.current, completedCrop);
        onCropComplete(croppedImageUrl);
      } catch (error) {
        console.error("[Cropper] Error cropping image:", error);
      }
    } else {
      console.warn("[Cropper] confirm blocked due to invalid crop or imgRef");
    }
  }, [completedCrop, getCroppedImg, onCropComplete]);

  const onImageLoad = (e) => {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
    console.log("[Cropper] image loaded", { width, height });
    const initialPercent = centerCrop(
      makeAspectCrop({ unit: "%", width: 60 }, 1, width, height),
      width,
      height
    );
    console.log("[Cropper] initial percent crop", initialPercent);
    setCrop(initialPercent);
    const initialPixels = {
      x: Math.round((initialPercent.x * width) / 100),
      y: Math.round((initialPercent.y * height) / 100),
      width: Math.round((initialPercent.width * width) / 100),
      height: Math.round((initialPercent.height * height) / 100),
      unit: "px",
    };
    console.log("[Cropper] initial pixel crop", initialPixels);
    setCompletedCrop(initialPixels);
  };

  return src ? (
    <div className="cropper-modal">
      <div className="cropper-content">
        <h3>Выберите область для обрезки</h3>
        <ReactCrop
          crop={crop}
          aspect={1}
          onChange={(_, percentCrop) => {
            if (percentCrop && percentCrop.width && percentCrop.height) {
              setCrop(percentCrop);
            }
          }}
          onComplete={(pixelCrop) => {
            console.log("[Cropper] complete", pixelCrop);
            if (pixelCrop && pixelCrop.width && pixelCrop.height) {
              setCompletedCrop(pixelCrop);
            }
          }}
          circularCrop={circular}
          keepSelection
        >
          <img
            ref={imgRef}
            alt="Source"
            src={src}
            onLoad={onImageLoad}
            style={{ maxWidth: "100%", maxHeight: "70vh" }}
          />
        </ReactCrop>
        <div className="cropper-actions">
          <button onClick={onCancel} className="upload-btn">
            Отмена
          </button>
          <button
            onClick={handleConfirm}
            disabled={!completedCrop}
            className="upload-btn"
          >
            Обрезать
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className="cropper-upload">
      <h3>Выберите изображение</h3>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={onSelectFile}
        style={{ display: "none" }}
      />
      <button onClick={() => fileInputRef.current?.click()}>
        Выбрать файл
      </button>
      <button onClick={onCancel} className="cancel-btn">
        Отмена
      </button>
    </div>
  );
};

export default ImageCropper;
